import Vue from 'vue';
import { cloneDeep, omit } from 'lodash';
import Moment from 'moment-timezone';
import { removableWord, treeTraversal } from '@/utils/main';
import { AutocompleteChipsTypes } from '@/utils/classes/AutocompleteChipsTypes';
import { Action } from '@/models/Action';
import { Cause } from '@/models/Cause';
import { Problem } from '@/models/Problem';
import * as types from './mutation-types';


/**
 * Set proper problem properties to be editable
 * @param  {Object} problem - new problem object
 * @param  {Object} oldProblem - previous value of the problem data
 * @return {Object} - the updated problem with all properties needed
 */
function setupProblem(problem, oldProblem = {}) {
  if (problem.keywords !== undefined && problem.keywords instanceof Array) {
    let html = '';
    for (let i = 0, len = problem.keywords.length; i < len; i++) {
      html += removableWord(problem.keywords[i].word, problem.keywords[i].id, AutocompleteChipsTypes.KEYWORD);
    }
    problem.editableKeywords = html;
  }
  if (!(oldProblem instanceof Problem)) {
    oldProblem = new Problem(oldProblem);
  }
  oldProblem.setProps(problem);
  return oldProblem;
}


const mutations = {

  /** Save problems using the filter configuration */
  [types.SET_PROBLEMS](state, { payload, data }) {
    const stateKey = JSON.stringify(omit(payload, 'limit'));
    const stateContent = state.problemsFetchConfigurations[stateKey] || { problemIds: [] };
    const newProblemIds = data.map(problem => problem.id);
    // Don't keep the previous IDs if creating from the beginning of the list
    const previousIdsToKeep = payload.limit && payload.limit.offset === 0
      ? []
      : stateContent.problemIds.filter(id => !newProblemIds.includes(id));
    stateContent.problemIds = [
      ...previousIdsToKeep,
      ...newProblemIds,
    ];
    Vue.set(state.problemsFetchConfigurations, stateKey, stateContent);
  },

  /** Update a problem in the state */
  [types.SET_PROBLEM](state, payload) {
    let newProblem = payload.problem || payload;
    const key = `${newProblem.id}`;
    newProblem = setupProblem(newProblem, state.problems[key]);
    Vue.set(state.problems, key, newProblem);

    // Set causes in the state
    treeTraversal(newProblem.wCauses || [], 'wCauses', (cause) => {
      const newCause = new Cause(cause);
      Vue.set(state.causes, `${newCause.id}`, newCause);
    });
  },

  /** Delete a problem in the state */
  [types.DELETE_PROBLEM](state, payload) {
    // Remove the id from any fetch configuration
    const problemId = payload.id;
    Object.keys(state.problemsFetchConfigurations).forEach((cfgKey) => {
      const stateContent = state.problemsFetchConfigurations[cfgKey];
      if (stateContent && stateContent.problemIds) {
        stateContent.problemIds = stateContent.problemIds.filter(id => id !== problemId);
      }
    });
    Vue.delete(state.problems, `${problemId}`);
  },

  /** Save actions using the filter configuration */
  [types.SET_ACTIONS](state, { payload, data }) {
    const stateKey = JSON.stringify(omit(payload, 'limit'));
    const stateContent = state.actionsFetchConfigurations[stateKey] || { actionIds: [] };
    const newActionIds = data.map(action => action.id);
    // Don't keep the previous IDs if creating from the beginning of the list
    const previousIdsToKeep = payload.limit && payload.limit.offset === 0
      ? []
      : stateContent.actionIds.filter(id => !newActionIds.includes(id));
    stateContent.actionIds = [
      ...previousIdsToKeep,
      ...newActionIds,
    ];
    Vue.set(state.actionsFetchConfigurations, stateKey, stateContent);
  },

  /** Update an action in the state */
  [types.SET_ACTION](state, payload) {
    // Update the action in the state
    const prevAction = state.actions[`${payload.id}`];
    const newAction = new Action({ ...(prevAction || {}), ...payload });
    Vue.set(state.actions, `${payload.id}`, newAction);
    // and update the problem is the action is linked to a problem
    const problem = state.problems[`${newAction.problem}`];
    if (problem && problem.actions) {
      const idx = problem.actions.findIndex(action => action.id === newAction.id);
      const { actions } = problem;
      if (idx >= 0) {
        actions[idx] = newAction;
      } else {
        actions.push(newAction);
      }
      Vue.set(problem, 'actions', actions);
    }
  },

  /** Delete an action in the state */
  [types.DELETE_ACTION](state, payload) {
    // Delete the action
    // Also delete from fetch configurations
    Object.keys(state.actionsFetchConfigurations).forEach((cfgKey) => {
      const stateContent = state.actionsFetchConfigurations[cfgKey];
      if (stateContent && stateContent.actionIds) {
        stateContent.actionIds = stateContent.actionIds.filter(id => id !== payload.id);
      }
    });
    Vue.delete(state.actions, `${payload.id}`);
    // Delete the action from the problem
    const actionToDelete = state.actions[`${payload.id}`] || { problem: payload.problemId || 0 };
    const problemId = actionToDelete.problem;
    const problem = state.problems[`${problemId}`];
    if (problem && problem.actions) {
      problem.actions.splice(problem.actions.findIndex(action => action.id === payload.id), 1);
      Vue.set(problem, 'actions', problem.actions);
    }
  },

  /** Update a problem cause */
  [types.SET_CAUSE](state, payload) {
    const problem = cloneDeep(state.problems[`${payload.problem}`]);
    const problems = [problem];
    // Get old cause
    let oldCause;
    treeTraversal(problems, 'wCauses', (parent) => {
      const idx = parent.wCauses.findIndex(childCause => childCause.id === payload.id);
      if (idx >= 0) {
        oldCause = parent.wCauses[idx];
        // Parent changed, remove cause from its parent
        if (oldCause.cause !== payload.cause) {
          parent.wCauses.splice(idx, 1);
        }
      }
    });
    const newCause = new Cause({ ...oldCause, ...payload });

    // Attach the cause to its new parent
    treeTraversal(problems, 'wCauses', (parent) => {
      if ((parent instanceof Problem && newCause.cause === 0)
        || (!(parent instanceof Problem) && parent.id === newCause.cause)
      ) {
        const idx = parent.wCauses.findIndex(childCause => childCause.id === payload.id);
        if (idx >= 0) {
          parent.wCauses.splice(idx, 1, newCause);
        } else {
          parent.wCauses.push(newCause);
        }
      }
    });
    // Update the wCauses of the problem
    Vue.set(state.problems, `${problems[0].id}`, problems[0]);
    // Update the cause
    Vue.set(state.causes, `${newCause.id}`, newCause);
  },

  /** Delete a problem cause, and attached the orphan causes to its parent (if any) */
  [types.DELETE_CAUSE](state, payload) {
    const problem = state.problems[`${payload.problem}`];
    const problems = [problem];

    treeTraversal(problems, 'wCauses', (parent) => {
      const idx = parent.wCauses.findIndex(childCause => childCause.id === payload.id);
      if (idx >= 0) {
        const toDelete = parent.wCauses[idx];
        const orphanCausesToAttach = toDelete.wCauses;
        parent.wCauses.splice(idx, 1);
        parent.wCauses = [...parent.wCauses, ...orphanCausesToAttach];
      }
    });

    Vue.delete(state.causes, `${payload.id}`);
  },

  [types.SET_PROBLEM_HISTORY](state, payload) {
    if (payload.problemId) {
      Vue.set(state.history, `${payload.problemId}`, payload.data);
      Vue.set(state.historyLoading, `${payload.problemId}`, false);
    }
  },

  [types.SET_HISTORY_LOADING](state, payload) {
    Vue.set(state.historyLoading, `${payload.problemId || 0}`, payload.loading === true);
  },

  // Following mutations are created to identify the infinite problem creation loop issue
  [types.LOG_CLB](state, payload) {
    if (typeof payload === 'string') {
      state.creationLogBuffer = `${state.creationLogBuffer}<br>\
        ${Moment().format('YYYY-MM-DD HH:mm:ss')} -=- ${payload}`;
    }
  },

  [types.INCREMENT_ADD_PROBLEM_CALLS](state) {
    state.latestProblemCalls.push(Moment());
  },

  // Following mutations are created to identify the infinite problem creation loop issue
  [types.PB_INTERRUPTION_MODAL_DISPLAYED](state, payload) {
    if (payload === true || payload === false) {
      state.problemInterruptionModalDisplayed = payload;
    }
  },

  // Following mutations are created to identify the infinite problem creation loop issue
  [types.RESUME_PROBLEM_CREATION](state, payload) {
    if (payload === true || payload === false) {
      state.interruptProblemCreation = !payload;
      Vue.set(state, 'latestProblemCalls', []);
    }
  },

  // Edit the accesses of a problem
  [types.SET_PROBLEM_ACCESSES](state, payload) {
    if (payload.id > 0 && payload.accesses instanceof Array) {
      const { id, accesses } = payload;
      Vue.set(state.problems, `${id}`, { ...state.problems[`${id}`], accesses });
    }
  },
};

export default mutations;
